import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase';
import { useNavigate } from 'react-router-dom';
import LogoImg from '../ChatGPT Image Jun 26, 2025, 12_35_25 AM.png';
import ThemeToggle from '../components/ThemeToggle';
import { runSupabaseAuthedOperation, toUserMessage } from '../utils/asyncReliability';
import { devLog, devError } from '../utils/logger';

const Logo = () => (
  <span className="flex items-center" style={{ alignItems: 'center' }}>
    <img src={LogoImg} alt="DevelloTech Logo" style={{ width: 96, height: 96, objectFit: 'contain', marginRight: 0, padding: 0, display: 'block' }} />
    <span
      className="text-3xl font-bold tracking-tight"
      style={{
        background: 'linear-gradient(90deg, #1761A0 0%, #2ED8C3 100%)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text',
        color: 'transparent',
        marginLeft: -12,
        paddingLeft: 0,
      }}
    >
      DevelloTech
    </span>
  </span>
);

const ClientDashboard = () => {
  const [, setUser] = useState(null);
  const [clientData, setClientData] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingQuote, setUpdatingQuote] = useState(null);
  const [showRequestChangesModal, setShowRequestChangesModal] = useState(false);
  const [selectedQuoteForChanges, setSelectedQuoteForChanges] = useState(null);
  const [requestChangesText, setRequestChangesText] = useState('');
  const [submittingChanges, setSubmittingChanges] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      const currentUser = session?.user ?? null;
      setUser(currentUser);

      if (event === 'TOKEN_REFRESHED') {
        return;
      }
      if (event === 'USER_UPDATED') {
        return;
      }

      if (!currentUser) {
        navigate('/client-login');
        return;
      }

      if (!currentUser.email_confirmed_at) {
        navigate('/verify-email');
        return;
      }

      // Load client data
      try {
        const { data: clientRows, error } = await supabase.from('clients').select('*').eq('id', currentUser.id);
        if (error) throw error;
        if (clientRows && clientRows.length > 0) {
          const client = clientRows[0];
          devLog('Loaded client data:', client);
          setClientData(client);
          
          // Load quotes for this client
          await loadClientQuotes(client.email);
        } else {
          // Not a client account
          await supabase.auth.signOut();
          navigate('/client-login');
        }
      } catch (error) {
        devError('Error loading client data:', error);
        await supabase.auth.signOut();
        navigate('/client-login');
      } finally {
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  const loadClientQuotes = async (clientEmail) => {
    try {
      devLog('Loading quotes for client email:', clientEmail);

      const { data: quotesData, error } = await supabase.from('quotes').select('*').eq('client_email', clientEmail);
      if (error) throw error;

      devLog('Found quotes:', (quotesData || []).length);

      // Sort quotes by creation date (most recent first)
      const sortedQuotes = (quotesData || []).sort((a, b) => {
        const dateA = a.created_at ? new Date(a.created_at) : new Date(0);
        const dateB = b.created_at ? new Date(b.created_at) : new Date(0);
        return dateB - dateA; // Most recent first
      });
      
      setQuotes(sortedQuotes);
    } catch (error) {
      devError('Error loading quotes:', error);
    }
  };

  const updateQuoteStatus = async (quoteId, newStatus) => {
    setUpdatingQuote(quoteId);

    try {
      // Update the quote status
      const updateRes = await runSupabaseAuthedOperation({
        name: 'clientDashboard:updateQuoteStatus',
        supabaseClient: supabase,
        timeoutMs: 45000,
        retries: 1,
        fn: async () => {
          const { error: updateError } = await supabase.from('quotes').update({
            status: newStatus,
            updated_at: new Date().toISOString(),
            statusHistory: [
              {
                status: newStatus,
                timestamp: new Date().toISOString(),
                updatedBy: 'client'
              }
            ]
          }).eq('id', quoteId);
          if (updateError) throw updateError;
        },
      });
      if (!updateRes.ok) throw updateRes.error || new Error(updateRes.userMessage);

      // Create notification for contractor
      const clientName = clientData?.name || clientData?.email || 'Unknown Client';
      const statusMessage = newStatus === 'accepted' 
        ? `Client ${clientName} accepted quote #${quoteId.slice(-6)}`
        : `Client ${clientName} rejected quote #${quoteId.slice(-6)}`;
      
      await createNotificationForContractor(
        quoteId,
        newStatus,
        statusMessage,
        clientName
      );

      // Update local state
      setQuotes(prev => prev.map(quote => 
        quote.id === quoteId 
          ? { ...quote, status: newStatus }
          : quote
      ));

    } catch (error) {
      devError('Error updating quote status:', error);
      alert(toUserMessage(error, 'Failed to update quote status. Please try again.'));
    } finally {
      setUpdatingQuote(null);
    }
  };

  const handleRequestChanges = (quote) => {
    setSelectedQuoteForChanges(quote);
    setRequestChangesText('');
    setShowRequestChangesModal(true);
  };

  const submitRequestChanges = async () => {
    if (!requestChangesText.trim()) {
      alert('Please enter your requested changes.');
      return;
    }

    setSubmittingChanges(true);
    try {
      const updateRes = await runSupabaseAuthedOperation({
        name: 'clientDashboard:requestChanges',
        supabaseClient: supabase,
        timeoutMs: 45000,
        retries: 1,
        fn: async () => {
          const { error: updateError } = await supabase.from('quotes').update({
            status: 'request_changes',
            updated_at: new Date().toISOString(),
            clientFeedback: requestChangesText,
            statusHistory: [
              {
                status: 'request_changes',
                timestamp: new Date().toISOString(),
                updatedBy: 'client',
                feedback: requestChangesText
              }
            ]
          }).eq('id', selectedQuoteForChanges.id);
          if (updateError) throw updateError;
        },
      });
      if (!updateRes.ok) throw updateRes.error || new Error(updateRes.userMessage);

      // Create notification for contractor
      const clientName = clientData?.name || clientData?.email || 'Unknown Client';
      await createNotificationForContractor(
        selectedQuoteForChanges.id,
        'request_changes',
        `Client ${clientName} requested changes to quote #${selectedQuoteForChanges.id.slice(-6)}`,
        clientName
      );

      // Update local state
      setQuotes(prev => prev.map(quote => 
        quote.id === selectedQuoteForChanges.id 
          ? { 
              ...quote, 
              status: 'request_changes',
              clientFeedback: requestChangesText
            }
          : quote
      ));

      setShowRequestChangesModal(false);
      setSelectedQuoteForChanges(null);
      setRequestChangesText('');
      // Success - no popup needed
    } catch (error) {
      devError('Error submitting request changes:', error);
      alert(toUserMessage(error, 'Failed to submit request changes. Please try again.'));
    } finally {
      setSubmittingChanges(false);
    }
  };

  // Create notification for contractor
  const createNotificationForContractor = async (quoteId, type, message, clientName) => {
    try {
      devLog('Creating notification for contractor:', { quoteId, type, message, clientName });
      
      // Get the quote to find the contractor's user ID
      const { data: quoteData, error: quoteError } = await supabase.from('quotes').select('*').eq('id', quoteId).single();
      
      if (quoteError) throw quoteError;
      if (quoteData) {
        devLog('Quote data:', quoteData);
        const contractorUserId = quoteData.user_id;
        
        if (contractorUserId) {
          // Create notification in contractor's user document
          const { data: userData, error: userError } = await supabase.from('profiles').select('*').eq('id', contractorUserId).single();
          
          if (userError) throw userError;
          if (userData) {
            const existingNotifications = Array.isArray(userData.notifications) ? userData.notifications : [];
            devLog('Existing notifications:', existingNotifications);
            const notification = {
              id: Date.now().toString(),
              quoteId: quoteId || '',
              type: type || '',
              message: message || '',
              clientName: clientName || '',
              timestamp: new Date().toISOString(),
              read: false
            };
            
            const updatedNotifications = [notification, ...existingNotifications];
            devLog('Updated notifications array:', updatedNotifications);
            
            const notifyRes = await runSupabaseAuthedOperation({
              name: 'clientDashboard:updateContractorNotifications',
              supabaseClient: supabase,
              timeoutMs: 45000,
              retries: 1,
              fn: async () => {
                const { error } = await supabase
                  .from('profiles')
                  .update({ notifications: updatedNotifications })
                  .eq('id', contractorUserId);
                if (error) throw error;
              },
            });
            if (!notifyRes.ok) throw notifyRes.error || new Error(notifyRes.userMessage);
            
            devLog('Notification created successfully:', notification);
            
            // Dispatch event to notify navbar to refresh notifications
            window.dispatchEvent(new Event('notification-updated'));
          } else {
            devLog('Contractor user document does not exist');
          }
        } else {
          devLog('No contractor user ID found in quote');
        }
      } else {
        devLog('Quote document does not exist');
      }
    } catch (error) {
      devError('Error creating notification for contractor:', error);
    }
  };

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
      navigate('/');
    } catch (error) {
      devError('Error signing out:', error);
    }
  };

  const calculateQuoteTotal = (quote) => {
    const subtotal = (quote.materials || []).reduce((sum, mat) => sum + (mat.qty * mat.price), 0);
    const markupAmount = subtotal * ((parseFloat(quote.markup) || 0) / 100);
    const subtotalAfterMarkup = subtotal + markupAmount;
    const hstAmount = subtotalAfterMarkup * 0.13;
    return subtotalAfterMarkup + hstAmount;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-950">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <Logo />
            
            <div className="flex items-center space-x-4">
              <ThemeToggle compact />
              <div className="text-right">
                <p className="text-sm font-medium text-gray-900">
                  {clientData?.fullName || 'Client'}
                </p>
                <p className="text-sm text-gray-500">{clientData?.email}</p>
              </div>
              <button
                onClick={handleSignOut}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Your Invoices</h1>
          <p className="text-gray-600 mt-2">
            View and manage invoices sent to you
          </p>
        </div>

        {quotes.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-4xl mb-4">📄</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No invoices yet</h3>
            <p className="text-gray-600">
              When your contractor sends an invoice, it will appear here.
            </p>
          </div>
        ) : (
          <div className="grid gap-6">
            {quotes.map((quote) => (
              <div key={quote.id} className="bg-white rounded-lg shadow-sm border p-6">
                <div className="mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Invoice #{quote.id.slice(-6)}
                    </h3>
                    <p className="text-sm text-gray-500">
                      Created: {quote.created_at ? new Date(quote.created_at).toLocaleDateString() : 'N/A'}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600 mb-4">
                  <div>
                    <span className="font-medium">Total Amount:</span>{' '}
                    <span className="font-semibold text-gray-900">
                      $
                      {calculateQuoteTotal(quote).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium">Description:</span> {quote.description || 'No description'}
                  </div>
                </div>

                <div className="flex justify-between items-center">
                  <div className="flex space-x-2">
                    {/* PDF View Button */}
                    {quote.pdfData && (
                      <button
                        onClick={() => {
                          // Convert base64 to blob and create PDF viewer
                          const byteCharacters = atob(quote.pdfData);
                          const byteNumbers = new Array(byteCharacters.length);
                          for (let i = 0; i < byteCharacters.length; i++) {
                            byteNumbers[i] = byteCharacters.charCodeAt(i);
                          }
                          const byteArray = new Uint8Array(byteNumbers);
                          const blob = new Blob([byteArray], { type: 'application/pdf' });
                          const url = URL.createObjectURL(blob);
                          
                          // Open PDF in new window/tab
                          window.open(url, '_blank');
                          
                          // Clean up after a delay
                          setTimeout(() => {
                            URL.revokeObjectURL(url);
                          }, 1000);
                        }}
                        className="text-blue-600 hover:text-blue-800 font-medium flex items-center"
                      >
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                        View PDF
                      </button>
                    )}
                    
                    {/* PDF Reference Message */}
                    {quote.pdfReference && !quote.pdfData && (
                      <div className="text-orange-600 font-medium flex items-center">
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                        PDF sent via email
                      </div>
                    )}
                    
                    {/* Quote Data Ready Message */}
                    {quote.pdfDataReady && !quote.pdfData && (
                      <div className="text-blue-600 font-medium flex items-center">
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Quote data ready - PDF being generated
                      </div>
                    )}
                  </div>
                  
                  {(quote.status === 'sent' || quote.status === 'sent_quote' || quote.status === 'sent_invoice' || quote.status === 'sent_both') && (
                    <div className="flex space-x-2">
                      <button
                        onClick={() => updateQuoteStatus(quote.id, 'accepted')}
                        disabled={updatingQuote === quote.id}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                      >
                        {updatingQuote === quote.id ? 'Updating...' : 'Accept'}
                      </button>
                      <button
                        onClick={() => updateQuoteStatus(quote.id, 'rejected')}
                        disabled={updatingQuote === quote.id}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                      >
                        {updatingQuote === quote.id ? 'Updating...' : 'Reject'}
                      </button>
                      <button
                        onClick={() => handleRequestChanges(quote)}
                        disabled={updatingQuote === quote.id}
                        className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors disabled:opacity-50"
                      >
                        {updatingQuote === quote.id ? 'Updating...' : 'Request Changes'}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Request Changes Modal */}
      {showRequestChangesModal && selectedQuoteForChanges && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold text-gray-900">
                Request Changes - Quote #{selectedQuoteForChanges.id.slice(-6)}
              </h3>
              <button 
                onClick={() => {
                  setShowRequestChangesModal(false);
                  setSelectedQuoteForChanges(null);
                  setRequestChangesText('');
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Please describe the changes you would like:
                </label>
                <textarea
                  value={requestChangesText}
                  onChange={(e) => setRequestChangesText(e.target.value)}
                  rows={8}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  placeholder="Please be specific about what changes you would like to the quote, materials, pricing, or any other details..."
                />
              </div>
              
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  onClick={() => {
                    setShowRequestChangesModal(false);
                    setSelectedQuoteForChanges(null);
                    setRequestChangesText('');
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={submitRequestChanges}
                  disabled={submittingChanges || !requestChangesText.trim()}
                  className="px-6 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submittingChanges ? 'Sending...' : 'Send Request'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default ClientDashboard; 